import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppState } from '../../Store';
import { ActionTypes } from '../../Store/Stores/User';
import { loginImage, logo } from '../../Resources/Images';
import { useTranslation } from '../../Translation';
import { useAPI } from '../../API';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { bgColor, checked, checkedBg, checkedBorder, classNames, color, disabledBg, focusOutline, focusVisible, hoverBgColor, hoverTextColor, indeterminateBg, indeterminateBorder, textColor } from '../../Resources/CSS/styling';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Checkbox from '../../Components/Inputs/Checkbox';
export default function Login() {

    const [form, setForm] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [didTryLogin, setDidTryLogin] = useState(false);
    const [validateMFA, setValidateMFA] = useState(false);
    const { pathAfterLogin } = useAppState();

    const { translate, language, setLanguage } = useTranslation();
    const api = useAPI();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const getUserAndRedirect = async () => {
        try {
            const userResponse = await api.getUser();

            dispatch({
                type: ActionTypes.LOGIN,
                payload: {}
            });

            dispatch({
                type: ActionTypes.SET_USER,
                payload: userResponse.data.user
            });

            const userRoleResponse = await api.getUserRole();
            dispatch({
                type: ActionTypes.SET_USER_ROLE,
                payload: userRoleResponse.data.userRoles
            });

            if (!userResponse.data.user.totp_secret) {
                navigate('/setup-totp');
                return;
            }

            navigate(pathAfterLogin);

            setTimeout(() => {
                setDidTryLogin(true);
            }, 1000);
        } catch (error) {
            console.error('Get user error:', error);
        } finally {
            setLoading(false);
            setDidTryLogin(true);
        }
    }

    useEffect(() => {
        getUserAndRedirect();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const response = await api.login(form.email, form.password, form.totp_code);

            // Dispatch successful login action
            dispatch({
                type: ActionTypes.LOGIN,
                payload: response.data
            });

            localStorage.removeItem('automatisations_tags_filter');
            localStorage.removeItem('campaigns_tags_filter');

            await getUserAndRedirect();
        } catch (error) {
            // Handle specific error cases
            if (error.status === 400) {
                // If the error is because the user needs to validate MFA, set the validateMFA state to true
                if (!validateMFA) {
                    setValidateMFA(true);
                    return;
                }
                else {
                    // If it was set to validateMFA and still not successful, set the validateMFA state to restart the login process
                    setValidateMFA(false);
                }

                setError(translate('login.invalidCredentials'));
            } else {
                setError(error.message || translate('login.genericError'));
            }
            console.error('Login error:', error);
        } finally {
            setLoading(false);
        }
    }

    if (!didTryLogin) {
        return <div></div>;
    }

    if (validateMFA) {
        return <ValidateMFA form={form} setForm={setForm} handleSubmit={handleSubmit} loading={loading} />;
    }

    return (
        <div className="flex min-h-full flex-1 w-full">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
                <div className="mx-auto w-full lg:w-96 border-gray-200 rounded-md p-8">

                    <div className="flex flex-col items-center mt-8 ">
                        <img src={logo} alt="logo" className="w-36" />
                        <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">{translate('signInToYourAccount')}</h2>
                    </div>

                    <div className="mt-10">
                        <div>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="rounded-md bg-red-50 p-4">
                                        <div className="flex">
                                            <div className="text-sm text-red-700">
                                                {error}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {loading && (
                                    <div className="flex justify-center items-center h-8 w-12">
                                        <LoadingCircleTransparent />
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                        {translate('login.email')}
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            required
                                            autoComplete="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className={classNames(`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 ${focusOutline}`)}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                                        {translate('login.password')}
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            id="password"
                                            name="password"
                                            type="password"
                                            required
                                            autoComplete="current-password"
                                            value={form.password}
                                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            className={classNames(`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 ${focusOutline}`)}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <Checkbox label={translate('login.rememberMe')} id="remember-me" checked={form.rememberMe} onChange={() => setForm({ ...form, rememberMe: !form.rememberMe })} />

                                    <div className="text-sm/6">
                                        <a href="#" className={classNames(`font-semibold ${textColor} ${hoverTextColor}`)}>
                                            {translate('login.forgotPassword')}
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={classNames(`flex w-full justify-center rounded-md ${bgColor} px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm ${hoverBgColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible} ${disabledBg}`)}
                                    >
                                        {translate('login.signIn')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {/* <div className="relative hidden w-0 flex-1 lg:block">
                <img
                    alt=""
                    src={loginImage}
                    className="absolute inset-0 size-full object-cover"
                />
            </div> */}
        </div>

    );
}

const ValidateMFA = ({ form, setForm, handleSubmit, loading }) => {
    const { translate } = useTranslation();

    return (
        <div className="flex min-h-full flex-1 w-full">
            <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
                <div className="mx-auto w-full lg:w-96 border-gray-200 rounded-md p-8">

                    <div className="flex flex-col items-center mt-8 ">
                        <img src={logo} alt="logo" className="w-36" />
                        <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">{translate('signInToYourAccount')}</h2>
                    </div>

                    <div className="mt-10">
                        <div>
                            <form onSubmit={handleSubmit} className="space-y-6">


                                <div>
                                    <label htmlFor="totp_code" className="block text-sm/6 font-medium text-gray-900">
                                        {translate('mfa.code_hint') || 'Enter the 6-digit code from your authenticator app.'}
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            id="totp_code"
                                            name="totp_code"
                                            type="text"
                                            required
                                            autoComplete="one-time-code"
                                            maxLength={6}
                                            pattern="[0-9]{6}"
                                            placeholder="000000"
                                            value={form.totp_code}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                setForm({ ...form, totp_code: value });
                                            }}
                                            className={classNames(`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 text-center text-2xl tracking-widest ${focusOutline}`)}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={classNames(`flex w-full justify-center rounded-md ${bgColor} px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm ${hoverBgColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible} ${disabledBg}`)}
                                    >
                                        {translate('login.signIn')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}